import React, { useId, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';

import { useVendorOptions } from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { Combobox } from 'shadcn/components/ui/Combobox';

/**
 * Renders a vendor picker select component.
 *
 * @param {Object} props Component props.
 * @param {string|number} [props.value] Selected vendor ID.
 * @param {Function} [props.onValueChange] Change handler invoked with the selected vendor ID.
 * @param {string} [props.label='Vendor'] Visible label rendered above the picker.
 * @param {boolean} [props.showLabel=true] Shows the label when true, hides it when false.
 * @returns {JSX.Element} Vendor picker component.
 */
export function VendorPicker({ label = 'Vendor', onValueChange, showLabel = true, value }) {
    const labelId = useId();
    const { data: vendorOptions = [], isFetching, isLoading } = useVendorOptions();

    // Options are already formatted as {value, label} from useVendorOptions
    const options = useMemo(
        () =>
            vendorOptions.map((option) => ({
                value: String(option.value),
                label: option.label,
            })),
        [vendorOptions],
    );

    /**
     * Extracts the ID from a value.
     * @param {string|number|object} val - The value to extract the ID from.
     * @returns {string|null} The ID as a string, or null if the value is null or not an object with an ID.
     */
    const extractId = (val) => {
        if (val == null) return null;
        if (typeof val === 'object' && val.id != null) {
            return String(val.id);
        }
        const numVal = Number(val);
        // If it's a valid number, return it as a string
        if (!isNaN(numVal) && isFinite(numVal)) {
            return String(numVal);
        }
        // Name-based lookup not available with useVendorOptions
        return null;
    };

    // Derive selected value as ID string (for Combobox)
    // Must match the option value type (string) for strict equality comparison
    const selectedValue = useMemo(() => {
        if (!value) return '';
        return extractId(value) ?? '';
    }, [value]);

    // Handle select change - return vendor ID
    const handleValueChange = (newValue) => {
        if (onValueChange) {
            const vendorId = newValue ? Number(newValue) : null;
            onValueChange(vendorId);
        }
    };

    // Show loading spinner when fetching and no data (or data is empty placeholder)
    const showLoading = isLoading || (isFetching && vendorOptions.length === 0);

    if (showLoading) {
        return (
            <div className="flex flex-col gap-2">
                {showLabel && label && (
                    <span
                        id={labelId}
                        className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                        {label}
                    </span>
                )}
                <div className="flex h-10 w-full items-center justify-center gap-2 rounded-md border border-input bg-card px-3 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    <span className="text-sm">Loading vendors…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {showLabel && label && (
                <span
                    id={labelId}
                    className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                    {label}
                </span>
            )}
            <Combobox
                id={labelId}
                options={options}
                value={selectedValue}
                handleSelect={handleValueChange}
                placeholder="Select vendor"
                disabled={!options.length || showLoading}
                popoverWidth="trigger"
                className="w-full"
                commandProps={{
                    filter: (value, search) => {
                        // Filter by label (vendor name)
                        const option = options.find((opt) => String(opt.value) === String(value));
                        if (!option) return false;
                        return option.label.toLowerCase().includes(search.toLowerCase());
                    },
                }}
            />
        </div>
    );
}

VendorPicker.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    onValueChange: PropTypes.func,
    showLabel: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

VendorPicker.defaultProps = {
    value: undefined,
    onValueChange: undefined,
    label: 'Vendor',
    showLabel: true,
};

VendorPicker.displayName = 'VendorPicker';
